import React, { useMemo, useState } from 'react';
import { CheckIcon, ChevronRightIcon, PlusIcon } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import type { RoleScopeGroup } from 'components/MapPage/CorporateViewTabs/NotificationsTab/audienceModel';

const SCOPE_TYPE_LABELS: Record<string, string> = {
  group: 'Org',
  aor: 'Area',
  incident: 'Incident',
};

type RoleScopePickerProps = {
  groups: RoleScopeGroup[];
  selectedKeys: ReadonlySet<string>;
  onAdd: (group: RoleScopeGroup) => void;
  loading?: boolean;
  disabled?: boolean;
};

export function RoleScopePicker({
  groups,
  selectedKeys,
  onAdd,
  loading = false,
  disabled = false,
}: RoleScopePickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return groups;
    return groups.filter(
      (group) =>
        group.roleName.toLowerCase().includes(term) ||
        group.scopeLabel.toLowerCase().includes(term)
    );
  }, [groups, search]);

  const toggleExpanded = (key: string, next: boolean) => {
    setExpanded((current) => {
      const updated = new Set(current);
      if (next) updated.add(key);
      else updated.delete(key);
      return updated;
    });
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setSearch('');
      }}
    >
      <PopoverTrigger
        render={
          <Button
            type="button"
            variant="outline"
            className="tw:w-full tw:justify-start tw:font-normal tw:text-muted-foreground"
            disabled={disabled}
          >
            <PlusIcon />
            Add a role and scope…
          </Button>
        }
      />
      <PopoverContent
        align="start"
        className="dark tw:w-(--anchor-width) tw:min-w-80 tw:gap-2 tw:p-2"
        positionerClassName="tw:z-[1110]"
      >
        <Input
          aria-label="Search roles and scopes"
          placeholder="Search roles and scopes…"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <div
          className="tw:flex tw:max-h-72 tw:flex-col tw:overflow-y-auto"
          role="list"
          aria-label="Roles and scopes"
        >
          {loading ? (
            <p className="tw:m-0 tw:p-2 tw:text-sm tw:text-muted-foreground">
              Loading roles…
            </p>
          ) : visible.length === 0 ? (
            <p className="tw:m-0 tw:p-2 tw:text-sm tw:text-muted-foreground">
              {groups.length === 0
                ? 'Nobody in this organization has a scoped role yet.'
                : 'No matches'}
            </p>
          ) : (
            visible.map((group) => {
              const selected = selectedKeys.has(group.key);
              const isExpanded = expanded.has(group.key);
              const scopeType = group.scopeType
                ? SCOPE_TYPE_LABELS[group.scopeType]
                : null;
              const count = group.members.length;
              return (
                <Collapsible
                  key={group.key}
                  open={isExpanded}
                  onOpenChange={(next) => toggleExpanded(group.key, next)}
                  role="listitem"
                >
                  <div className="tw:flex tw:items-center tw:gap-1 tw:rounded-md tw:hover:bg-accent">
                    <CollapsibleTrigger
                      render={
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-xs"
                          aria-label={`${isExpanded ? 'Hide' : 'Show'} people with ${group.roleName} · ${group.scopeLabel}`}
                        >
                          <ChevronRightIcon
                            className={cn(
                              'tw:transition-transform',
                              isExpanded && 'tw:rotate-90'
                            )}
                          />
                        </Button>
                      }
                    />
                    <button
                      type="button"
                      disabled={selected}
                      onClick={() => {
                        onAdd(group);
                        setOpen(false);
                        setSearch('');
                      }}
                      className="tw:flex tw:min-w-0 tw:flex-1 tw:items-center tw:gap-2 tw:bg-transparent tw:py-1.5 tw:pr-1 tw:text-left tw:text-sm tw:text-foreground tw:disabled:cursor-default tw:disabled:opacity-60"
                    >
                      <span className="tw:min-w-0 tw:flex-1 tw:break-words">
                        {group.roleName} · {group.scopeLabel}
                        {scopeType ? (
                          <span className="tw:ml-1.5 tw:text-xs tw:text-muted-foreground">
                            {scopeType}
                          </span>
                        ) : null}
                      </span>
                      {selected ? (
                        <CheckIcon
                          className="tw:size-4 tw:shrink-0"
                          aria-label="Already added"
                        />
                      ) : null}
                      <Badge
                        variant="secondary"
                        className="tw:shrink-0"
                        aria-label={`${count} ${count === 1 ? 'user' : 'users'}`}
                      >
                        {count}
                      </Badge>
                    </button>
                  </div>
                  <CollapsibleContent>
                    <ul className="tw:m-0 tw:mb-1 tw:ml-8 tw:list-none tw:p-0 tw:text-sm tw:text-muted-foreground">
                      {group.members.map((member) => (
                        <li key={member.userGuid} className="tw:py-0.5">
                          {member.displayName}
                        </li>
                      ))}
                    </ul>
                  </CollapsibleContent>
                </Collapsible>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
